import { Form, Lead } from "@/types";
import { calculateTotalInvestment } from "@/utils/achievement-utils";

export type Achievement = {
  id: string;
  title: string;
  description: string;
  earned: boolean;
  color: string;
  isAdvanced?: boolean;
};

export function getInitialAchievements(forms: Form[], leads: Lead[]): Achievement[] {
  return [
    {
      id: "joined",
      title: "Spark Ignited",
      description: "Your fundraising journey has officially begun!",
      earned: true, // User is logged in, so this is always earned
      color: "bg-purple-500",
      isAdvanced: false,
    },
    {
      id: "created_form",
      title: "Form Forger",
      description: "You crafted your first investor outreach form",
      earned: forms.length > 0,
      color: "bg-blue-500",
      isAdvanced: false,
    },
    {
      id: "embed_form",
      title: "Web Weaver",
      description: "Your form is now embedded on your website and seen by visitors",
      earned: forms.some(form => form.views >= 3),
      color: forms.some(form => form.views >= 3) ? "bg-green-500" : "bg-gray-300",
      isAdvanced: false,
    },
    {
      id: "first_visitor",
      title: "First Contact",
      description: "Someone discovered your investor form on your site",
      earned: forms.some(form => form.views >= 5),
      color: forms.some(form => form.views >= 5) ? "bg-amber-500" : "bg-gray-300",
      isAdvanced: false,
    },
    {
      id: "first_lead",
      title: "Capital Pioneer",
      description: "You've captured your first potential investor's interest",
      earned: leads.length > 0,
      color: leads.length > 0 ? "bg-indigo-500" : "bg-gray-300",
      isAdvanced: false,
    },
    {
      id: "100_views",
      title: "Century Viewer",
      description: "Your forms were viewed by 100 potential investors",
      earned: forms.reduce((total, form) => total + form.views, 0) >= 100,
      color: forms.reduce((total, form) => total + form.views, 0) >= 100 ? "bg-orange-500" : "bg-gray-300",
      isAdvanced: false,
    },
    {
      id: "25_leads",
      title: "Quarter-Century Club",
      description: "You've built a potential investor pool of 25 interested parties",
      earned: leads.length >= 25,
      color: leads.length >= 25 ? "bg-pink-500" : "bg-gray-300",
      isAdvanced: false,
    },
    {
      id: "10k_interest",
      title: "10K Milestone",
      description: "$10,000 in potential funding is on the horizon",
      earned: calculateTotalInvestment(leads) >= 10000,
      color: calculateTotalInvestment(leads) >= 10000 ? "bg-yellow-500" : "bg-gray-300",
      isAdvanced: false,
    },
  ];
}

export function getAdvancedAchievements(forms: Form[], leads: Lead[]): Achievement[] {
  return [
    {
      id: "50_leads",
      title: "Investor Magnet",
      description: "Your campaign has attracted 50 potential investors to your cause",
      earned: leads.length >= 50,
      color: leads.length >= 50 ? "bg-emerald-500" : "bg-gray-300",
      isAdvanced: true,
    },
    {
      id: "25k_interest",
      title: "Quarter Cap",
      description: "You've garnered $25,000 in potential investment interest",
      earned: calculateTotalInvestment(leads) >= 25000,
      color: calculateTotalInvestment(leads) >= 25000 ? "bg-cyan-500" : "bg-gray-300",
      isAdvanced: true,
    },
    {
      id: "100_leads",
      title: "Century Circle",
      description: "Your network has expanded to 100 interested investors",
      earned: leads.length >= 100,
      color: leads.length >= 100 ? "bg-violet-500" : "bg-gray-300",
      isAdvanced: true,
    },
    {
      id: "50k_interest",
      title: "50K Fundraiser",
      description: "Your pitch has generated $50,000 in potential investment interest",
      earned: calculateTotalInvestment(leads) >= 50000,
      color: calculateTotalInvestment(leads) >= 50000 ? "bg-rose-500" : "bg-gray-300",
      isAdvanced: true,
    },
    {
      id: "200_leads",
      title: "Double Century",
      description: "Your investor outreach has connected with 200 potential backers",
      earned: leads.length >= 200,
      color: leads.length >= 200 ? "bg-fuchsia-500" : "bg-gray-300",
      isAdvanced: true,
    },
    {
      id: "100k_interest",
      title: "Six-Figure Club",
      description: "You've reached the elite $100,000 potential investment milestone",
      earned: calculateTotalInvestment(leads) >= 100000,
      color: calculateTotalInvestment(leads) >= 100000 ? "bg-lime-500" : "bg-gray-300",
      isAdvanced: true,
    },
    {
      id: "500_leads",
      title: "Investor Army",
      description: "You've built a formidable force of 500 potential investors",
      earned: leads.length >= 500,
      color: leads.length >= 500 ? "bg-sky-500" : "bg-gray-300",
      isAdvanced: true,
    },
    {
      id: "500k_interest",
      title: "Half-Million Hero",
      description: "Your campaign has attracted $500,000 in potential investment",
      earned: calculateTotalInvestment(leads) >= 500000,
      color: calculateTotalInvestment(leads) >= 500000 ? "bg-amber-600" : "bg-gray-300",
      isAdvanced: true,
    },
  ];
}
